<template>
  <validation-observer ref="manualAdjustmentFormRef" novalidate tag="form">
    <div class="mx-4">
      <div class="row mb-3 pt-3">
        <div class="col-12">
          <icon-view
            icon="fa fa-file-edit"
            icon-background="#bb64c6"
            icon-color="#fff"
            icon-size="14px"
            title="Ajuste Manual de Asistencia"
            variant="custom-icon-view"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-3 col-sm col-6">
          <validation-provider
            v-slot="{ errors }"
            rules="required|differentFromZero"
          >
            <custom-input
              id="txtAmount"
              v-model="model.amount"
              custom-error-msg="Debe completar campo"
              :error="errors[0]"
              label="Ajuste:"
              mask-type="Money"
              prepend-icon="fa fa-dollar-sign"
              :prepend-icon-left="true"
              prepend-icon-type="success"
            />
          </validation-provider>
        </div>
        <div
          class="col-3 col-sm col-12 order-2 order-md-0 order-sm-1 mt-3 mt-md-0 mt-sm-3"
        >
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-drop-down
              id="ddAdjustmentReasons"
              v-model="model.reason"
              class="custom-drop-down"
              custom-error-msg="Debe seleccionar una Razón"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              label="Razón de Ajuste"
              :options="reasons"
            />
          </validation-provider>
        </div>
        <div class="col-6 pl-0 pl-md-5 pl-sm-0 pt-2">
          <div class="row">
            <div class="col-6">
              <div class="color-secondary-6 font-14 font-weight-normal">
                Total antes de Ajuste:
              </div>
              <div class="d-flex align-items-center">
                <span class="fa-stack fa-1x color-yellow-star font-14 mr-3">
                  <em class="fas fa-circle fa-stack-2x" />
                  <em class="fas fa-dollar-sign fa-stack-1x fa-inverse" />
                </span>
                <span
                  class="color-green font-26 font-weight-normal"
                  :class="amountClass(totalAfterAdjustment)"
                >
                  {{ totalAttendedAmount | price }}
                </span>
              </div>
            </div>
            <div class="col-6">
              <div class="color-secondary-6 font-14 font-weight-normal">
                Total luego de Ajuste:
              </div>
              <div class="d-flex align-items-center">
                <span class="fa-stack fa-1x color-secondary-30 font-14 mr-3">
                  <em class="fas fa-circle fa-stack-2x" />
                  <em class="fas fa-dollar-sign fa-stack-1x fa-inverse" />
                </span>
                <span
                  class="color-green font-26 font-weight-normal"
                  :class="amountClass(totalAfterAdjustment)"
                >
                  {{ totalAfterAdjustment | price }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 mt-3">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-text-area
              id="txtComment"
              v-model="model.comment"
              custom-error-msg="Debe completar campo."
              :error="errors[0]"
              label="Comentario:"
              :maxlength="500"
              show-char-count
            />
          </validation-provider>
        </div>
      </div>
      <div class="row mt-3 mt-md-0 mt-sm-3 pb-3">
        <div
          class="col-12 d-flex align-items-center justify-content-md-end justify-content-sm-none"
        >
          <action-button
            v-if="hasManualAdjustment"
            i="fas fa-trash"
            variant="danger btn-cancel danger px-3 mr-2"
            @click="onDelete()"
          >
            Remover
          </action-button>
          <action-button
            i="fas fa-times-circle"
            variant="outline-danger btn-cancel danger px-3 mr-2"
            @click="onCancel()"
          >
            Cancelar
          </action-button>
          <action-button
            i="fas fa-save"
            variant="success px-3"
            @click="onConfirm()"
          >
            Guardar
          </action-button>
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import IconView from 'icon-view';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';

export default {
  name: 'ManualAdjustment',
  components: {
    ValidationObserver,
    ValidationProvider,
    IconView,
    CustomInput,
    CustomDropDown,
    CustomTextArea,
    ActionButton,
  },
  props: {
    costTotalAmount: {
      type: Number,
      default: () => 0,
    },
    reasons: {
      type: Array,
      default: () => [],
    },
    assistanceSummaryId: {
      type: Number,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    totalAttendedAmount: {
      type: Number,
      default: () => 0,
    },
    manualAmount: {
      type: Number,
      default: () => 0,
    },
    reasonSelected: {
      type: Object,
      default: null,
    },
    reasonComment: {
      type: String,
      default: '',
    },
    hasManualAdjustment: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      model: {
        amount: this.manualAmount,
        reason: this.reasonSelected,
        comment: this.reasonComment,
      },
    };
  },
  computed: {
    totalAfterAdjustment() {
      return this.totalAttendedAmount + parseFloat(this.model.amount);
    },
    amountClass() {
      return (price) =>
        Math.sign(price) >= 0 ? 'color-green' : 'color-secondary-28';
    },
  },
  watch: {
    manualAmount: {
      handler(newValue) {
        this.model.amount = newValue;
      },
      immediate: true,
    },
  },
  methods: {
    onDelete() {
      this.$emit('on-delete', this.index, this.assistanceSummaryId);
    },
    onCancel() {
      this.$emit('on-cancel');
    },
    async onConfirm() {
      const isValid = await this.$refs.manualAdjustmentFormRef.validate();
      if (!isValid) {
        this.ShowToast(
          'Alerta',
          'Todos los campos deben ser completados para completar la acción',
          'error'
        );
        return;
      }

      let items = [
        {
          assistanceSummaryId: this.assistanceSummaryId,
          adjustmentReasonCd: this.model.reason.id,
          comment: this.model.comment,
          amount: parseFloat(this.model.amount),
        },
      ];

      this.$emit('on-confirm', this.index, items);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .custom-drop-down {
  .vs__dropdown-toggle {
    height: 38px;
  }
}

@media only screen and (max-width: 768px) {
}
</style>
